import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, FileText, Instagram, Mail, Phone, MapPin } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import Logo from "@/components/Logo";

const navItems = [
  { label: "Accueil", path: "/" },
  {
    label: "Afrique",
    children: [
      { label: "Ethiopie", path: "/destinations/ethiopie" },
      { label: "Djibouti", path: "/destinations/djibouti" },
    ]
  },
  {
    label: "Asie",
    children: [
      { label: "Myanmar", path: "/destinations/myanmar" },
      { label: "Vietnam Sapa", path: "/destinations/vietnam-sapa" },
      { label: "Laos", path: "/destinations/laos" },
      { label: "Cambodge", path: "/destinations/cambodge" },
      { label: "Thailande Isan", path: "/destinations/thailande-isan" },
      { label: "Lombok", path: "/destinations/lombok" },
    ]
  },
  {
    label: "Amerique du Sud",
    children: [
      { label: "Perou", path: "/destinations/perou" },
    ]
  },
  { label: "Autres Destinations", path: "/autres-destinations" },
  { label: "Blog", path: "/blog" },
];

function DesktopDropdown({ item }) {
  return (
    <div className="nav-item relative group" data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
      <button className="flex items-center gap-1 font-body text-sm tracking-wide text-[#2C2C2C] hover:text-[#2D5016] transition-colors py-2">
        {item.label}
        <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:rotate-180" />
      </button>
      <div className="nav-dropdown absolute top-full left-0 pt-2 z-50">
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-[#DBCFB0]/50 py-2 min-w-[200px]">
          {item.children.map((child, i) => (
            <Link
              key={i}
              to={child.path}
              className="block px-4 py-2.5 text-sm text-[#2C2C2C] hover:bg-[#FDF8F0] hover:text-[#2D5016] transition-colors"
              data-testid={`nav-link-${child.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              {child.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNav({ open, onClose }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[300px] bg-[#FDF8F0] p-0 border-r-[#DBCFB0]">
        <div className="p-6">
          <Logo size="small" />
        </div>
        <nav className="px-4 pb-6">
          {navItems.map((item, i) => (
            <div key={i} className="border-b border-[#DBCFB0]/40">
              {item.children ? (
                <div>
                  <button
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="w-full flex items-center justify-between py-3 px-2 text-[#2C2C2C] font-body text-sm"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${expanded === i ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded === i && (
                    <div className="pb-2 pl-4">
                      {item.children.map((child, j) => (
                        <Link
                          key={j}
                          to={child.path}
                          onClick={() => onClose(false)}
                          className="block py-2 px-2 text-sm text-[#6B6B6B] hover:text-[#2D5016]"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => onClose(false)}
                  className="block py-3 px-2 text-[#2C2C2C] font-body text-sm"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
          <Link
            to="/demande-de-devis"
            onClick={() => onClose(false)}
            className="mt-6 block text-center bg-[#2D5016] text-[#FDF8F0] rounded-full py-3 px-6 font-body text-sm font-medium"
            data-testid="mobile-cta-devis"
          >
            Demander un devis
          </Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#FDF8F0]/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
            : "bg-[#FDF8F0]/70 backdrop-blur-sm"
        }`}
        data-testid="main-header"
      >
        <nav className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          <Link to="/" data-testid="header-logo-link">
            <Logo />
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item, i) =>
              item.children ? (
                <DesktopDropdown key={i} item={item} />
              ) : (
                <Link
                  key={i}
                  to={item.path}
                  className="font-body text-sm tracking-wide text-[#2C2C2C] hover:text-[#2D5016] transition-colors py-2"
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </div>

          <div className="hidden lg:block">
            <Link
              to="/demande-de-devis"
              className="bg-[#2D5016] text-[#FDF8F0] rounded-full px-6 py-2.5 font-body text-sm font-medium hover:bg-[#2D5016]/90 transition-all shadow-lg hover:shadow-xl"
              data-testid="header-cta-devis"
            >
              Demander un devis
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6 text-[#2C2C2C]" />
          </button>
        </nav>
      </header>

      {/* Mobile navigation */}
      <MobileNav open={mobileOpen} onClose={setMobileOpen} />

      {/* Main content */}
      <main className="pt-20">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-[#2D5016] text-[#FDF8F0]" data-testid="main-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Col 1 - Logo & info */}
            <div>
              <div className="mb-6">
                <span className="font-heading text-2xl font-bold text-[#FDF8F0]">VISITMONDE</span>
                <p className="text-xs text-[#C8A951] tracking-widest mt-1">LA BOUSSOLE ETHNIQUE</p>
              </div>
              <div className="space-y-2 text-sm text-[#FDF8F0]/80">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>6, place de la Fontaine<br />78670 Villennes sur Seine</span>
                </div>
                <p className="text-xs text-[#FDF8F0]/60 mt-3">RCS 811 258 046</p>
                <p className="text-xs text-[#FDF8F0]/60">IM 078 150006</p>
              </div>
            </div>

            {/* Col 2 - Nav links */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4 text-[#C8A951]">Destinations</h4>
              <div className="space-y-2">
                {[
                  { label: "Accueil", path: "/" },
                  { label: "Nos destinations", path: "/autres-destinations" },
                  { label: "Blog", path: "/blog" },
                  { label: "Demande de devis", path: "/demande-de-devis" },
                ].map((link, i) => (
                  <Link key={i} to={link.path} className="block text-sm text-[#FDF8F0]/80 hover:text-[#C8A951] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Col 3 - Legal */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4 text-[#C8A951]">Informations</h4>
              <div className="space-y-2">
                {[
                  { label: "Mentions legales", path: "/mentions-legales" },
                  { label: "CGV", path: "/cgv" },
                  { label: "Politique de confidentialite", path: "/politique-de-confidentialite" },
                  { label: "Qui sommes-nous", path: "/qui-sommes-nous" },
                ].map((link, i) => (
                  <Link key={i} to={link.path} className="block text-sm text-[#FDF8F0]/80 hover:text-[#C8A951] transition-colors">
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Col 4 - Social & contact */}
            <div>
              <h4 className="font-heading text-lg font-semibold mb-4 text-[#C8A951]">Contact</h4>
              <div className="space-y-3 text-sm text-[#FDF8F0]/80">
                <a href="mailto:dominicimarc64@gmail.com" className="flex items-center gap-2 hover:text-[#C8A951] transition-colors underline cursor-pointer">
                  <Mail className="w-4 h-4" /> contact@visitmonde.com
                </a>
                <div className="flex gap-3 mt-4">
                  <a
                    href="https://instagram.com/visitmonde"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#FDF8F0]/10 flex items-center justify-center hover:bg-[#C8A951]/30 transition-colors"
                    data-testid="footer-instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#FDF8F0]/20 mt-12 pt-6 text-center text-xs text-[#FDF8F0]/50">
            &copy; 2025 VisitMonde - Tous droits reserves
          </div>
        </div>
      </footer>

      {/* Floating CTA - mobile */}
      <Link
        to="/demande-de-devis"
        className="floating-cta fixed bottom-20 right-4 z-40 lg:hidden bg-[#C8A951] text-[#2C2C2C] rounded-full px-5 py-3 shadow-lg flex items-center gap-2 font-body text-sm font-medium hover:shadow-xl transition-shadow"
        data-testid="floating-cta-devis"
      >
        <FileText className="w-4 h-4" />
        Devis
      </Link>
    </div>
  );
}
